<script>
export default {
  name: 'ActionButton',
  inheritAttrs: false,
  props: {
    label: {
      type: String,
      default: '',
    },
    iconName: {
      type: String,
      default: '',
    },
    iconOnly: {
      type: Boolean,
      default: false,
    },
    disabledTooltipMessage: {
      type: String,
      default: '',
    },
    defaultIconName: {
      type: String,
      default: 'setting',
    },
    iconColor: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isDisabled() {
      return this.disabled
    },
    isLoading() {
      return this.loading
    },
    resolvedIconName() {
      if (this.iconName) return this.iconName
      if (this.iconOnly) return this.defaultIconName
      return ''
    },
    tooltipMessage() {
      if (this.isDisabled && this.disabledTooltipMessage) {
        return this.disabledTooltipMessage
      }
      if (this.iconOnly && this.label) {
        return this.label
      }
      return ''
    },
    hasTooltip() {
      return Boolean(this.tooltipMessage)
    },
    resolvedIconColor() {
      if (this.iconColor) return this.iconColor
      if (this.isDisabled) return undefined
      if (!this.iconOnly) return undefined

      const attrs = this.$attrs || {}
      const colorKeys = [
        'primary',
        'secondary',
        'success',
        'alert',
        'warning',
        'dark',
        'light',
      ]

      const foundKey = colorKeys.find((key) =>
        Object.prototype.hasOwnProperty.call(attrs, key)
      )

      return foundKey
    },
    iconClassList() {
      const classList = ['base-action-button__icon']

      if (this.isDisabled) {
        classList.push('base-action-button__icon--disabled')
      }

      return classList
    },
    wrapperClassList() {
      const classList = ['base-action-button__tooltip-activator']

      if (this.isDisabled && this.iconOnly) {
        classList.push('base-action-button__tooltip-activator--disabled')
      }

      return classList
    },
  },
}
</script>

<template>
  <span class="base-action-button">
    <v-tooltip
      v-if="hasTooltip"
      top
      class="base-action-button__tooltip-wrapper"
    >
      <template v-slot:activator="{ on, attrs }">
        <span v-bind="attrs" :class="wrapperClassList" v-on="on">
          <v-btn
            v-if="iconOnly"
            icon
            v-bind="$attrs"
            :loading="isLoading"
            :disabled="isDisabled"
            class="base-action-button__icon-button"
            v-on="$listeners"
          >
            <icon-svg
              v-if="resolvedIconName"
              :name="resolvedIconName"
              :color="resolvedIconColor"
              :class="iconClassList"
            />
          </v-btn>

          <BaseButton
            v-else
            v-bind="$attrs"
            :loading="isLoading"
            :disabled="isDisabled"
            v-on="$listeners"
          >
            <icon-svg
              v-if="resolvedIconName"
              :name="resolvedIconName"
              :color="resolvedIconColor"
              :class="iconClassList"
            />

            <span v-if="label" class="base-action-button__label">
              {{ label }}
            </span>
          </BaseButton>
        </span>
      </template>

      <span>
        {{ tooltipMessage }}
      </span>
    </v-tooltip>

    <v-btn
      v-else-if="iconOnly"
      icon
      v-bind="$attrs"
      :loading="isLoading"
      :disabled="isDisabled"
      class="base-action-button__icon-button"
      v-on="$listeners"
    >
      <icon-svg
        v-if="resolvedIconName"
        :name="resolvedIconName"
        :color="resolvedIconColor"
        :class="iconClassList"
      />
    </v-btn>

    <BaseButton
      v-else
      v-bind="$attrs"
      :loading="isLoading"
      :disabled="isDisabled"
      v-on="$listeners"
    >
      <icon-svg
        v-if="resolvedIconName"
        :name="resolvedIconName"
        :color="resolvedIconColor"
        :class="iconClassList"
      />

      <span v-if="label" class="base-action-button__label">
        {{ label }}
      </span>
    </BaseButton>
  </span>
</template>

<style lang="sass" scoped>
.base-action-button
  display: inline-flex
  align-items: center

  &__tooltip-wrapper
    display: inline-flex

  &__tooltip-activator
    display: inline-flex

  &__tooltip-activator--disabled
    cursor: not-allowed

  &__icon-button
    display: inline-flex
    align-items: center
    justify-content: center

  &__icon
    display: inline-flex
    align-items: center
    justify-content: center

  &__icon--disabled
    opacity: 0.5

  &__label
    display: inline-flex
    align-items: center
</style>
